import { IconButton } from '@mui/material';
import { VyTooltip } from '../../../../../../../../components/new-design/VyTooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import { PERMISSIONS } from '@vy/types';
import React from 'react';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../../../../components/new-design/feedback/VyDialog';
import { useCancelInvitation } from '../../../../../../../../hooks/requests/use-cancel-invitation';
import { useDialog } from '../../../../../../../../hooks/use-dialog';
import { queryKeys } from '../../../../../../../../services/query-key/query-keys';
import { renderWithPermissions } from '../../../../../../../../services/render-with-permissions';
import { runMutation } from '../../../../../../../../utils/vy-mutation';

type CancelInvitationButtonProps = {
  invitationId: string;
  userEmail: string;
};

export const CancelInvitationButton: React.FC<CancelInvitationButtonProps> = ({
  invitationId,
  userEmail,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const cancelInvitationMutation = useCancelInvitation();
  const queryClient = useQueryClient();

  const handleCancelInvitation = async () => {
    handleClose();
    await runMutation(
      cancelInvitationMutation,
      { invitationId },
      {
        pending: `Canceling invitation for ${userEmail}...`,
        success: `Invitation for ${userEmail} has been canceled`,
        error: `Failed to cancel invitation for ${userEmail}`,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(queryKeys.invitations());
          await queryClient.invalidateQueries(queryKeys.users());
        },
      },
    );
  };

  return renderWithPermissions([PERMISSIONS.USER_INVITE], (hasPermission) => (
    <>
      <VyTooltip tooltipContent="Cancel invitation">
        <IconButton onClick={handleOpen} size="small" disabled={!hasPermission}>
          <DeleteIcon />
        </IconButton>
      </VyTooltip>

      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label={`Cancel invitation for ${userEmail}`}
        labelDescription={`You are about to cancel the invitation for '${userEmail}'`}
        warningText="Once canceled, the user will not be able to join your organization with this invitation"
        cancelButtonText="Cancel"
        ctaText="Cancel Invitation"
        ctaDisabled={cancelInvitationMutation.isLoading}
        onSubmit={handleCancelInvitation}
      />
    </>
  ));
};
